import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Grid, TextField, MenuItem, Snackbar, Alert } from '@mui/material';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { postTeam, updateTeam, getTeamPerId } from 'services/team';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';

const LEVEL_OPTIONS = [
    { id: 1, label: '1 estrela - Prata', defaultDays: 15 },
    { id: 2, label: '2 estrelas - Prata', defaultDays: 45 },
    { id: 3, label: '3 estrelas - Prata', defaultDays: 105 },
    { id: 4, label: '4 estrelas - Prata', defaultDays: 120 },
    { id: 5, label: '5 estrelas - Prata', defaultDays: 150 },
    { id: 6, label: '1 estrela - Ouro', defaultDays: 365 }
];

const Team = () => {
    const themeButton = createTheme({
        status: {
            danger: '#e53e3e'
        },
        palette: {
            primary: {
                main: '#0971f1',
                darker: '#053e85'
            },
            neutral: {
                main: '#64748B',
                contrastText: '#fff'
            }
        }
    });
    const theme = useTheme();
    const params = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [team, setTeam] = useState([]);
    const [valuesEdit, setValuesEdit] = useState({ description: '' });
    const isDisabled = params.action === 'view';
    const idUnit = useSelector((state) => state.user.unit || '');

    useEffect(() => {
        if (params.action) {
            getTeamPerId(params.id).then((resp) => {
                setValuesEdit({ description: resp.data.data.description });
                setTeam(
                    resp.data.data.level_experience.map((desc) => ({
                        id: desc.id,
                        label: desc.label,
                        days: desc.days
                    }))
                );
            });
        } else {
            // Adiciona todos os níveis com dias padrão ao iniciar
            const defaultLevels = LEVEL_OPTIONS.map((lvl) => ({
                id: lvl.id,
                label: lvl.label,
                days: lvl.defaultDays
            }));
            setTeam(defaultLevels);
        }
    }, [params.id, params.action]);

    const handleChangeDays = (id, value) => {
        setTeam((prevTeam) => prevTeam.map((item) => (item.id === id ? { ...item, days: value } : item)));
    };

    return (
        <Formik
            initialValues={{ description: valuesEdit.description }}
            enableReinitialize
            validationSchema={Yup.object().shape({
                description: Yup.string().max(45, 'Permitido no máximo 45 caracteres').trim().required('Nome da equipe obrigatório')
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting, resetForm }) => {
                try {
                    setSubmitting(false);
                    const data = { description: values.description, team };
                    setLoading(true);
                    const actionFn = params.action === 'edit' ? updateTeam : postTeam;
                    const actionParams = params.action === 'edit' ? [params.id, data] : [data];

                    actionFn(...actionParams)
                        .then((resp) => {
                            setSuccess(resp.data.success);
                            setTimeout(() => setSuccess(''), 3000);
                            if (params.action !== 'edit') {
                                const resetLevels = LEVEL_OPTIONS.map((lvl) => ({ id: lvl.id, label: lvl.label, days: lvl.defaultDays }));
                                setTeam(resetLevels);
                                setValuesEdit({ description: '' });
                            }
                            resetForm();
                        })
                        .catch((e) => {
                            setError(e.response?.data?.error || 'Erro ao salvar');
                            setTimeout(() => setError(''), 3000);
                            console.log(e);
                        })
                        .finally(() => setLoading(false));
                } catch (err) {
                    setStatus({ success: false });
                    setErrors({ submit: err.message });
                    console.log(err);
                }
            }}
        >
            {({ errors, handleChange, handleBlur, handleSubmit, isSubmitting, touched, values }) => (
                <MainCard style={{ padding: 15, margin: 25 }}>
                    <h1>Turma</h1>
                    {(error || success) && (
                        <Snackbar open autoHideDuration={6000}>
                            <Alert severity={error ? 'error' : 'success'}>{error || success}</Alert>
                        </Snackbar>
                    )}
                    <form noValidate onSubmit={handleSubmit}>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome da Turma"
                                    name="description"
                                    value={values.description}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(touched.description && errors.description)}
                                    helperText={touched.description && errors.description}
                                    disabled={isDisabled}
                                />
                            </Grid>
                        </Grid>

                        <h3 style={{ marginTop: 32 }}>Níveis</h3>
                        {team.map((desc) => (
                            <Grid key={desc.id} container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Nível" value={desc.label} disabled />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Dias"
                                        type="number"
                                        value={desc.days}
                                        onChange={(e) => handleChangeDays(desc.id, e.target.value)}
                                        disabled={isDisabled}
                                    />
                                </Grid>
                            </Grid>
                        ))}

                        <Grid container justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Grid item>
                                <Box sx={{ mt: 2, mr: 2 }}>
                                    <ThemeProvider theme={themeButton}>
                                        <AnimateButton>
                                            <Button
                                                disableElevation
                                                disabled={isSubmitting}
                                                fullWidth
                                                size="large"
                                                type="button"
                                                variant="contained"
                                                color="neutral"
                                                onClick={() => navigate(-1)}
                                            >
                                                Voltar
                                            </Button>
                                        </AnimateButton>
                                    </ThemeProvider>
                                </Box>
                            </Grid>
                            {params.action === 'view' ? (
                                <Grid item>
                                    <Box sx={{ mt: 2, mr: 3 }}>
                                        <AnimateButton>
                                            <Button
                                                disableElevation
                                                // disabled={isSubmitting}
                                                component={Link}
                                                to={`/nivel/${params.id}/edit`}
                                                fullWidth
                                                size="large"
                                                variant="contained"
                                                color="primary"
                                            >
                                                Editar
                                            </Button>
                                        </AnimateButton>
                                    </Box>
                                </Grid>
                            ) : (
                                <Grid item>
                                    <Box sx={{ mt: 2, mr: 3 }}>
                                        <AnimateButton>
                                            <Button
                                                disableElevation
                                                disabled={isSubmitting}
                                                fullWidth
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                            >
                                                Salvar
                                            </Button>
                                        </AnimateButton>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </form>
                </MainCard>
            )}
        </Formik>
    );
};

export default Team;
